import { useRef, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Pin, PinOff } from "lucide-react";
import { useFeedbackStore, type FeedbackItem } from "../../stores/feedback.js";
import { FeedbackMessage } from "./FeedbackMessage.js";
import { formatDistanceToNow } from "date-fns";

interface FeedbackTimelineProps {
  filters: Set<string>;
  search: string;
  runId?: string;
}

export function FeedbackTimeline({ filters, search, runId }: FeedbackTimelineProps) {
  const messages = useFeedbackStore((s) => s.messages);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = useState(true);

  const filtered = filterMessages(messages, filters, search, runId);
  const grouped = groupByTime(filtered);

  // Auto-scroll to bottom
  useEffect(() => {
    if (autoScroll && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length, autoScroll]);

  // Detect manual scroll
  function handleScroll() {
    if (!scrollRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 40;
    if (autoScroll && !isAtBottom) setAutoScroll(false);
    if (!autoScroll && isAtBottom) setAutoScroll(true);
  }

  return (
    <div className="h-full flex flex-col relative">
      {/* Auto-scroll toggle */}
      <button
        onClick={() => {
          setAutoScroll(!autoScroll);
          if (!autoScroll && scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
          }
        }}
        className={`absolute top-2 right-3 z-10 p-1.5 rounded-lg transition-[color,background-color,transform] duration-200 active:scale-[0.96] ${
          autoScroll
            ? "bg-brand-purple/10 text-brand-purple"
            : "bg-surface-lighter/20 text-text-muted hover:text-text-primary"
        }`}
        title={autoScroll ? "Auto-scroll on" : "Auto-scroll off"}
      >
        {autoScroll ? (
          <Pin className="w-3.5 h-3.5" />
        ) : (
          <PinOff className="w-3.5 h-3.5" />
        )}
      </button>

      {/* Timeline */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto"
      >
        {filtered.length === 0 ? (
          <div className="flex items-center justify-center h-full text-sm text-text-muted">
            {messages.length === 0
              ? "Activity will appear here when a run starts"
              : "No messages match your filters"}
          </div>
        ) : (
          <div className="timeline-line">
            {grouped.map((group) => (
              <div key={group.label}>
                {/* Time group header */}
                <div className="sticky top-0 z-[5] px-4 py-1.5 bg-surface/80 backdrop-blur-sm">
                  <span className="text-[10px] text-text-muted font-medium uppercase tracking-wider">
                    {group.label}
                  </span>
                </div>

                {/* Messages */}
                {group.messages.map((msg, i) => (
                  <motion.div
                    key={msg.id}
                    initial={i === group.messages.length - 1 ? { opacity: 0, y: 8 } : false}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.15 }}
                  >
                    <FeedbackMessage msg={msg} />
                  </motion.div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Helpers ──

function filterMessages(
  messages: FeedbackItem[],
  filters: Set<string>,
  search: string,
  runId?: string,
): FeedbackItem[] {
  return messages.filter((msg) => {
    if (filters.size > 0 && !filters.has(msg.type)) return false;
    if (runId && msg.runId !== runId) return false;
    if (search) {
      const content = JSON.stringify(msg.payload).toLowerCase();
      const source = (msg.source?.name ?? "").toLowerCase();
      if (!content.includes(search.toLowerCase()) && !source.includes(search.toLowerCase())) {
        return false;
      }
    }
    return true;
  });
}

interface TimeGroup {
  label: string;
  messages: FeedbackItem[];
}

function groupByTime(messages: FeedbackItem[]): TimeGroup[] {
  if (messages.length === 0) return [];

  const now = Date.now();
  const groups: TimeGroup[] = [];
  let currentGroup: TimeGroup | null = null;

  for (const msg of messages) {
    const ts = new Date(msg.timestamp).getTime();
    const diff = now - ts;

    let label: string;
    if (diff < 60_000) label = "Just now";
    else if (diff < 5 * 60_000) label = "A few minutes ago";
    else label = formatDistanceToNow(ts, { addSuffix: true });

    if (!currentGroup || currentGroup.label !== label) {
      currentGroup = { label, messages: [] };
      groups.push(currentGroup);
    }
    currentGroup.messages.push(msg);
  }

  return groups;
}
